// src/components/tutor/SageActionCard.tsx
//
// The Start button under a Sage reply (QA finding 7, 2026-10: asked "What
// should I study next?", Sage recommended a short Boundaries drill and gave the
// student nothing to click).
//
// Renders a SageAction (packages/core/src/sage-action.ts) — an action the
// SERVER built and the caller has already run through parseSageAction. The
// card adds no URL of its own: the link is exactly `action.href`, which the
// core allow-list pins to the one page each kind may open. So whatever Sage
// wrote in its text, the button opens the activity, topic and session size the
// server checked.
//
// Shared by the thread surfaces (ToolCard, in the /tutor page and the FAB) and
// the per-question chat (TutorChat), so a Start button looks and behaves the
// same everywhere.

'use client'

import type { MouseEvent, ReactNode } from 'react'
import Link from 'next/link'
import { ArrowRight, BookOpen, ClipboardCheck, Flag, Target } from 'lucide-react'
import { sageActionTrackId, type SageAction, type SageActionDifficulty } from '@unlimitedtests/core'

const DIFFICULTY_TEXT: Record<SageActionDifficulty, string> = {
  E: 'Easy',
  M: 'Medium',
  H: 'Hard',
  mixed: 'Mixed difficulty',
}

/**
 * The small line under the label: what kind of thing this is, plus the facts
 * the label doesn't already say. The label carries the count and topic ("5
 * Boundaries questions"), so this never repeats them. Null when there's
 * nothing to add.
 */
export function sageActionMeta(action: SageAction): string | null {
  const parts: string[] = []
  switch (action.kind) {
    case 'drill':
      parts.push('Practice set')
      if (action.difficulty) parts.push(DIFFICULTY_TEXT[action.difficulty])
      break
    case 'practice':
      parts.push('Topic practice')
      break
    case 'skill_check':
      if (action.count !== null) {
        parts.push(`${action.count} ${action.count === 1 ? 'question' : 'questions'}`)
      }
      break
    case 'lesson':
    case 'first_step':
      // The label says it all ("Boundaries lesson", "Take the Checkpoint");
      // a first step's duration arrives as the note.
      break
  }
  if (action.note) parts.push(action.note)
  return parts.length > 0 ? parts.join(' · ') : null
}

const KIND_ICON: Record<SageAction['kind'], ReactNode> = {
  drill: <Target className="h-4 w-4" aria-hidden="true" />,
  practice: <Target className="h-4 w-4" aria-hidden="true" />,
  skill_check: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
  lesson: <BookOpen className="h-4 w-4" aria-hidden="true" />,
  first_step: <Flag className="h-4 w-4" aria-hidden="true" />,
}

/** The path part of a same-origin href ("/practice/session?ids=…" → "/practice/session"). */
function pathOf(href: string): string {
  const q = href.search(/[?#]/)
  return q === -1 ? href : href.slice(0, q)
}

export type SageActionCardProps = {
  /** A parsed action (parseSageAction). Never build one here. */
  action: SageAction
  /**
   * Called when the student launches the action in this tab (a plain click —
   * not a cmd/ctrl/shift-click into a new tab or window). The FAB uses it to
   * close its overlay so the panel doesn't cover the session it just opened.
   */
  onLaunch?: (action: SageAction) => void
  /** Spacing for the card's container; replaces the default `my-2`. */
  className?: string
}

export default function SageActionCard({ action, onLaunch, className }: SageActionCardProps) {
  const meta = sageActionMeta(action)

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    if (e.defaultPrevented) return
    // A modified click opens a new tab or window and leaves this page as it is,
    // so the panel stays where the student left it. next/link skips these too.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    onLaunch?.(action)
    // Same page, different query: App Router keeps the page mounted when only
    // the search params change, and /practice/session loads its questions once
    // per mount. A client navigation from a topic session to a Sage drill
    // would swap the header and keep serving the old questions. A full load
    // mounts the page fresh on the new link.
    if (typeof window !== 'undefined' && pathOf(action.href) === window.location.pathname) {
      e.preventDefault()
      window.location.assign(action.href)
    }
  }

  return (
    <div
      className={
        'flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border border-brand/30 bg-brand/10 p-3 dark:bg-brand/20 ' +
        (className ?? 'my-2')
      }
    >
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <span className="mt-0.5 shrink-0 text-brand-ink">{KIND_ICON[action.kind]}</span>
        <div className="min-w-0">
          <p className="break-words text-sm font-medium text-foreground">{action.label}</p>
          {meta && <p className="mt-0.5 text-xs text-muted-foreground">{meta}</p>}
        </div>
      </div>
      {/* Same tab, deliberately: the activity IS the next step, and a new tab
          would leave the student with two copies of the app. The aria-label
          starts with the visible text so voice control still matches "Start",
          and names the activity so a screen reader's link list can tell two
          Start buttons apart. No prefetch: every route renders per request
          (the root layout reads the session), and a long thread replays a card
          per old recommendation — prefetching each would be a server render
          per card for links the student mostly won't click. */}
      <Link
        href={action.href}
        prefetch={false}
        onClick={handleClick}
        data-track={sageActionTrackId(action)}
        aria-label={`${action.cta}: ${action.label}`}
        className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-md bg-brand px-4 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
      >
        {action.cta}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </div>
  )
}
